import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/layout/Layout";
import BillingSystem from "../components/page/BillingSystem";
import SettlementAdmin from "../components/page/SettlementAdmin";
import BillingCatalogAdmin from "../components/page/BillingCatalogAdmin";
import RevenueDashboard from "../components/page/RevenueDashboard";
import AllCustomersAdmin from "../components/page/AllCustomersAdmin";
import ChannelDetailAdmin from "../components/page/ChannelDetailAdmin";
import SystemSettingAdmin from "../components/page/SystemSettingAdmin";
import SystemStatusAdmin from "../components/page/SystemStatusAdmin";
import IncidentAdmin from "../components/page/IncidentAdmin";
import { getIncidentSummary } from "../api/system/incident.api";

/**
 * 시스템 관리자(/sysadmin) 셸 — A2, 2026-08-04 경로 분리.
 *
 * 종전에는 한 페이지 안에서 `useState` 탭으로 전환했다. 새로고침하면 첫 탭으로 돌아가고,
 * 특정 화면을 링크로 공유할 수 없으며, 뒤로가기가 sysadmin 밖으로 나가 버렸다.
 * → **경로가 곧 화면**이 되도록 바꿨다.
 *
 *   /sysadmin                     → /sysadmin/dashboard 로 리다이렉트(App.js)
 *   /sysadmin/dashboard           수익 대시보드
 *   /sysadmin/client              전체 고객(메인 원장)
 *   /sysadmin/client/:channelId   고객사 상세 — 목록에서 진입, URL 로 직접 접근도 가능
 *   /sysadmin/billing             정산 관리
 *   /sysadmin/price               정가표
 *   /sysadmin/legacy-billing      (legacy) 빌링
 *   /sysadmin/setting             설정(플래그 색상 · 런타임 설정 system_config) — 2026-08-06
 *   /sysadmin/status              배포 버전(운영 파드의 커밋·빌드 시각) — 2026-09-16
 *
 * '배포 버전' 은 라우트를 새로 넣지 않았다 — `/sysadmin/:section` 이 이미 이 셸로 오므로
 * 아래 MENU 에 한 줄 추가하고 렌더 분기를 다는 것으로 끝난다(`App.js` 변경 0).
 *
 * '구독 관리'·'플래그'·'일회성 비용'은 독립 화면에서 제거하고 **고객사 단위**(전체 고객 / 고객사 상세)로
 * 옮겼다 — 같은 데이터를 두 곳에서 고칠 수 있으면 어느 쪽이 최신인지 알 수 없게 된다.
 * 구 컴포넌트 `SubscriptionAdmin.js`·`FlagAdmin.js`·`OneTimeFeeAdmin.js` 는 더 이상 라우팅되지 않는다.
 */
const MENU = [
  { key: "dashboard", label: "대시보드" },
  { key: "client", label: "전체 고객" },
  { key: "billing", label: "정산 관리" },
  { key: "price", label: "정가표" },
  { key: "legacy-billing", label: "(legacy) 빌링" },
  { key: "setting", label: "설정" },
  { key: "status", label: "배포 버전" },
  { key: "incidents", label: "사고 현황" }, // incident_log — 2026-10-30. 미해결 건수를 탭에 배지로 보여준다
];

const SystemAdminPage = () => {
  const { section, channelId } = useParams();
  const navigate = useNavigate();

  // /sysadmin/client/:channelId 로 들어오면 section 이 비어 있다 → client 로 취급.
  const current = channelId != null ? "client" : (section ?? "dashboard");

  const openDetail = useCallback((id) => navigate(`/sysadmin/client/${id}`), [navigate]);
  const closeDetail = useCallback(() => navigate("/sysadmin/client"), [navigate]);

  const known = MENU.some((m) => m.key === current);

  // 사고 미해결 건수 배지 — "열어 봐야 아는 것" 을 "안 볼 수 없는 것" 으로. 실패해도 셸이 깨지면 안 되므로 삼킨다.
  const [openIncidents, setOpenIncidents] = useState(null);
  useEffect(() => {
    let alive = true;
    getIncidentSummary().then((s) => { if (alive) setOpenIncidents(s?.unresolvedTotal ?? null); }).catch(() => {});
    return () => { alive = false; };
  }, [current]);

  return (
    <Layout>
      <nav className="flex gap-2 px-6 pt-4 border-b">
        {MENU.map((m) => (
          <button
            key={m.key}
            className={`px-3 py-1.5 rounded-t ${
              current === m.key ? "bg-white border border-b-0 font-semibold -mb-px" : "bg-gray-100 text-gray-500"
            }`}
            onClick={() => navigate(`/sysadmin/${m.key}`)}
          >
            {m.label}
            {m.key === "incidents" && openIncidents > 0 && (
              <span className="ml-1 rounded-full bg-red-600 px-1.5 text-[11px] font-semibold text-white">{openIncidents}</span>
            )}
          </button>
        ))}
      </nav>

      {!known && (
        <div className="p-6 text-sm text-gray-500">
          알 수 없는 화면입니다. 위 메뉴에서 선택해 주세요. (요청 경로: /sysadmin/{section})
        </div>
      )}

      {current === "dashboard" && <RevenueDashboard />}
      {current === "client" && channelId == null && <AllCustomersAdmin onOpenDetail={openDetail} />}
      {current === "client" && channelId != null && (
        <ChannelDetailAdmin
          channelId={channelId}
          onChannelChange={(id) => navigate(`/sysadmin/client/${id}`, { replace: true })}
          onBack={closeDetail}
        />
      )}
      {current === "billing" && <SettlementAdmin onOpenDetail={openDetail} />}
      {current === "incidents" && <IncidentAdmin />}
      {current === "price" && <BillingCatalogAdmin />}
      {current === "legacy-billing" && <BillingSystem />}
      {current === "setting" && <SystemSettingAdmin />}
      {current === "status" && <SystemStatusAdmin />}
    </Layout>
  );
};
export default SystemAdminPage;
